import React from 'react';
import { useField } from 'formik';
import styled, { CSSProperties } from 'styled-components/macro';
import { FormikErrorMessage } from './FormikErrorMessage';
import { FormikLabel } from './FormikLabel';
import { theme } from 'lib/style';
import { Flex, FlexProps } from '../styles/layout';

export const TextInput = styled.input`
  font-style: normal;
  font-weight: normal;
  font-size: 16px;
  line-height: 24px;
  color: #272a32;
  border-radius: 4px;
  height: 40px;
  width: 100%;
  box-sizing: border-box;
  border: solid 1px #e1e2e5;
  &:focus {
    outline: none;
    border: solid 1px ${theme.palette.covideoBlue100};
  }
  &:disabled {
    cursor: not-allowed;
  }

  padding: 8px 36px 8px 12px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const InputWithUnitWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const UnitLabel = styled.span`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: ${({ theme }) => theme.colors.neutral[60]};
  font-size: 14px;
  pointer-events: none;
  background: white;
`;

interface CustomInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  name: string;
  label?: string;
  info?: string | React.ReactNode;
  isRequired: boolean;
  suffix?: string;
  hideError?: boolean;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  extendStyles?: {
    errorWrapper?: CSSProperties;
    labelStyles?: CSSProperties;
    asteriskStyles?: CSSProperties;
    inputWrapper?: FlexProps;
    mainWrapper?: FlexProps;
  };
}

export const FormikNumberField = React.forwardRef<
  HTMLInputElement,
  CustomInputProps
>(
  (
    {
      label,
      isRequired,
      extendStyles,
      suffix,
      info,
      hideError = false,
      onBlur,
      ...props
    },
    ref
  ) => {
    const [field, meta, helpers] = useField(props);

    // Custom onChange for number inputs
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      helpers.setValue(value === '' ? '' : Number(value));
    };

    // On blur, if number is empty, set it to 0
    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      if (!!onBlur) {
        onBlur(e);
        field.onBlur(e);
        return;
      }
      if (field.value === '') {
        helpers.setValue(0);
      }
      field.onBlur(e);
    };

    const inputProps = {
      ...field,
      ...props,
      id: props.name,
      ref,
      onChange: handleChange,
      onBlur: handleBlur,
    };

    return (
      <Flex {...extendStyles?.mainWrapper} gap='4px'>
        {label && (
          <FormikLabel
            label={label}
            name={props.name}
            isRequired={isRequired}
            disabled={!!props.disabled}
            info={info}
            labelStyles={extendStyles?.labelStyles}
            asteriskStyles={extendStyles?.asteriskStyles}
          />
        )}
        <Flex {...extendStyles?.inputWrapper} gap='4px'>
          <InputWithUnitWrapper>
            <TextInput {...inputProps} type='number' />
            {suffix && <UnitLabel>{suffix}</UnitLabel>}
          </InputWithUnitWrapper>
          {!hideError && (
            <FormikErrorMessage meta={meta} extendStyles={extendStyles} />
          )}
        </Flex>
      </Flex>
    );
  }
);

FormikNumberField.displayName = 'FormikNumberField';
